/**
 * Single row in the prepared drops drafts list.
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  useCallback,
  type CSSProperties,
  type FC,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import { Pencil, Send } from 'lucide-react';

import { Button, LoadingSpinner } from '@/components/ui';
import { useToast } from '@/providers/ToastProvider';
import { ResendKilledControls } from '@/features/resend-killed-drops';
import { cn } from '@/lib/utils';

import { draftsRowGridStyle, draftsRowGridWithCountryStyle } from '../../shared/grid';
import { SYSTEM_LIST_TAGS } from '../../shared/tags/systemListTags';
import { countryToFlagEmoji, countryKeyToIso2 } from '@/utils/country-flag-emoji';
import { CreativeCell, LocalSendCell, StatusCell } from './cells';
import { LeadsListTags } from '@/features/smswiser-campaigns/components/shared';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { formatDateTimeDMYHM } from '@/features/smswiser-campaigns/utils/dateFormat';

export interface PreparedDropsDraftsListRowProps {
  drop: PreparedDrop;
  costPerSms: string | null;
  totalCost: string | null;
  isSelected: boolean;
  isBusy: boolean;
  isSending?: boolean;
  isKilling?: boolean;
  gatewayName: string;
  leadsListName: string;
  leadsListTags?: string[];
  tagColors?: Record<string, string>;
  returnTo?: string;
  showCountryColumn?: boolean;
  /** The Drop Generator is writing creative for this country right now. */
  creativeFilling?: boolean;
  rowGridStyle?: CSSProperties;
  onToggleSelected: (id: string, checked: boolean) => void;
  onDraftRowPointerDown?: (e: PointerEvent<HTMLElement>, dropId: string) => void;
  onDraftRowPointerEnter?: (dropId: string) => void;
  consumeDraftRowClickSuppress?: () => boolean;
  onEdit: (id: string | null) => void;
  onSend: (preparedDropId: string) => void;
  onEditSent?: (drop: PreparedDrop) => void | Promise<void>;
}

export const PreparedDropsDraftsListRow: FC<PreparedDropsDraftsListRowProps> = ({
  drop,
  costPerSms,
  totalCost,
  isSelected,
  isBusy,
  isSending = false,
  isKilling = false,
  gatewayName,
  leadsListName,
  leadsListTags = [],
  tagColors,
  returnTo,
  showCountryColumn = false,
  creativeFilling = false,
  rowGridStyle,
  onToggleSelected,
  onDraftRowPointerDown,
  onDraftRowPointerEnter,
  consumeDraftRowClickSuppress,
  onEdit,
  onSend,
  onEditSent,
}) => {
  const { success: toastSuccess, error: toastError } = useToast();
  const performanceTags = (leadsListTags ?? []).filter((t) => !SYSTEM_LIST_TAGS.has(t));

  const handleCopyListName = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      if (!leadsListName) return;
      try {
        await navigator.clipboard.writeText(leadsListName);
        toastSuccess('Copied', 'List name copied to clipboard.');
      } catch {
        toastError('Copy failed', 'Could not copy to clipboard.');
      }
    },
    [leadsListName, toastSuccess, toastError],
  );

  const leadsNameEl = leadsListName ? (
    <button
      type="button"
      onClick={handleCopyListName}
      onPointerDown={(e) => e.stopPropagation()}
      className="min-w-0 max-w-full truncate whitespace-nowrap text-left text-foreground hover:underline"
      title={`Click to copy “${leadsListName}”`}
    >
      {leadsListName}
    </button>
  ) : (
    <span className="whitespace-nowrap opacity-50">—</span>
  );

  const isDraft = drop.status === 'DRAFT';
  const isScheduled =
    drop.status === 'SENT' &&
    drop.executeAfter &&
    !Number.isNaN(new Date(drop.executeAfter).getTime()) &&
    new Date(drop.executeAfter).getTime() > Date.now();

  const timeDisplay = (() => {
    if (isDraft) return formatDateTimeDMYHM(drop.createdAt);
    const ts = isScheduled
      ? drop.executeAfter
      : drop.status === 'KILLED'
        ? drop.finishedAt || drop.sentAt
        : drop.sentAt || drop.finishedAt;
    if (!ts || Number.isNaN(new Date(ts).getTime())) return '—';
    return formatDateTimeDMYHM(ts);
  })();

  return (
    <div
      role="button"
      tabIndex={0}
      onPointerDown={(e) => onDraftRowPointerDown?.(e, drop.id)}
      onPointerEnter={() => onDraftRowPointerEnter?.(drop.id)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('a, button')) return;
        if (consumeDraftRowClickSuppress?.()) return;
        onToggleSelected(drop.id, !isSelected);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggleSelected(drop.id, !isSelected);
        }
      }}
      className={cn(
        'grid w-full min-w-0 cursor-pointer items-center gap-x-3 px-4 py-2.5 text-sm leading-none',
        onDraftRowPointerDown && isDraft && 'select-none',
        isSelected ? 'bg-primary/25 hover:bg-primary/35' : 'hover:bg-muted/30',
        isSending && 'bg-primary/10',
        isKilling && 'bg-destructive/5',
      )}
      style={rowGridStyle ?? (showCountryColumn ? draftsRowGridWithCountryStyle : draftsRowGridStyle)}
    >
      {showCountryColumn ? (
        <div
          className="flex min-w-0 items-center gap-1 text-xs text-foreground"
          title={drop.countryKey}
        >
          <span className="text-base leading-none">{countryToFlagEmoji(drop.countryKey)}</span>
          <span className="font-medium uppercase text-muted-foreground">
            {countryKeyToIso2(drop.countryKey) || '—'}
          </span>
        </div>
      ) : null}
      <div className="min-w-0 overflow-hidden truncate text-foreground">
        {gatewayName || <span className="opacity-50">—</span>}
      </div>
      <CreativeCell
        senderNames={drop.senderNames ?? []}
        smsTexts={drop.smsTexts ?? []}
        filling={
          creativeFilling && drop.status === 'DRAFT' && drop.source === 'GENERATOR'
        }
      />
      <div className="flex min-w-0 items-center gap-1.5">
        {leadsNameEl}
        {performanceTags.length > 0 && (
          <LeadsListTags performanceTags={performanceTags} tagColors={tagColors} compact />
        )}
      </div>
      <div className="min-w-0 truncate text-xs text-muted-foreground" title={drop.campaignName ?? undefined}>
        {drop.campaignName ? <span className="truncate">{drop.campaignName}</span> : <span className="opacity-50">—</span>}
      </div>
      <div className="flex min-w-0 justify-end font-medium tabular-nums text-foreground">
        {costPerSms ?? '—'}
      </div>
      <div className="flex min-w-0 justify-end font-medium tabular-nums text-foreground">
        {drop.amount.toLocaleString()}
      </div>
      <div
        className={cn(
          'flex min-w-0 justify-end font-medium tabular-nums',
          drop.status === 'KILLED' ? 'text-muted-foreground' : 'text-destructive',
        )}
      >
        {totalCost ?? '—'}
      </div>
      <div className="min-w-0 truncate font-medium text-foreground">
        {drop.shortLinkTldValue ? drop.shortLinkTldValue : <span className="opacity-50">—</span>}
      </div>
      <div className="min-w-0">
        {drop.staticTag ? (
          <span
            className="inline-flex min-w-0 max-w-full truncate rounded px-1.5 py-0.5 text-[10px] font-medium text-white"
            style={{ backgroundColor: drop.staticTag.color }}
            title={drop.staticTag.name}
          >
            {drop.staticTag.name}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </div>
      <div
        className="min-w-0 truncate text-xs tabular-nums text-muted-foreground"
        title={drop.executeAfter ?? undefined}
      >
        {drop.executeAfter && !Number.isNaN(new Date(drop.executeAfter).getTime()) ? (
          formatDateTimeDMYHM(drop.executeAfter)
        ) : (
          <span className="opacity-50">—</span>
        )}
      </div>
      <LocalSendCell executeAfter={drop.executeAfter} countryKey={drop.countryKey} />
      <div className="min-w-0 text-xs tabular-nums text-muted-foreground">{timeDisplay}</div>
      <StatusCell drop={drop} isSending={isSending} isKilling={isKilling} returnTo={returnTo} />
      <div className="flex min-w-0 items-center justify-end gap-1">
        {isDraft ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 shrink-0 p-0"
              disabled={isBusy}
              onClick={() => onEdit(drop.id)}
              aria-label="Edit draft"
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 shrink-0 p-0"
              disabled={isBusy || isSending}
              onClick={() => onSend(drop.id)}
              aria-label="Send draft"
              title="Send"
            >
              {isSending ? (
                <LoadingSpinner size="sm" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </>
        ) : drop.status === 'SENT' && onEditSent && (drop.sentDropId != null || drop.sentDropUniqueId) ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 shrink-0 p-0"
            disabled={isBusy}
            onClick={() => onEditSent(drop)}
            aria-label="Edit sent drop"
            title="Edit"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        ) : drop.status === 'KILLED' ? (
          <ResendKilledControls drops={[drop]} variant="row" disabled={isBusy} />
        ) : null}
      </div>
    </div>
  );
};
